import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { API, SITE } from "../data/site";
import { CATEGORIES, productLabelFor } from "../data/products";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const PRODUCT_OPTIONS = ["General Enquiry", ...CATEGORIES.map((c) => c.name)];

const Field = ({ label, required, children, id }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
      {label} {required && <span className="text-safety">*</span>}
    </label>
    {children}
  </div>
);

const inputCls =
  "border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-navy placeholder:text-slate-400 transition-colors focus:border-safety focus:outline-none rounded-none";

const EnquiryForm = ({ defaultProduct = "" }) => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    designation: "",
    email: "",
    phone: "",
    city_state: "",
    product_of_interest: productLabelFor(defaultProduct) || "General Enquiry",
    quantity: "",
    message: "",
    website: "",
    recaptcha_token: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (defaultProduct) {
      const label = productLabelFor(defaultProduct);
      if (label) setForm((f) => ({ ...f, product_of_interest: label }));
    }
  }, [defaultProduct]);

  useEffect(() => {
    if (SITE_KEY && !document.getElementById("recaptcha-v3-script")) {
      const s = document.createElement("script");
      s.id = "recaptcha-v3-script";
      s.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const getToken = () =>
    new Promise((resolve) => {
      if (!SITE_KEY || !window.grecaptcha) return resolve("");
      window.grecaptcha.ready(() =>
        window.grecaptcha
          .execute(SITE_KEY, { action: "enquiry" })
          .then(resolve)
          .catch(() => resolve(""))
      );
    });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError("Please fill in your name, email and phone.");
      return;
    }
    setStatus("loading");
    try {
      const token = await getToken();
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, recaptcha_token: token }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Submission failed. Please try again.");
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(err.message || "Something went wrong. Please call or WhatsApp us.");
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="border border-emerald-200 bg-emerald-50 p-8 text-center"
        data-testid="enquiry-success-panel"
      >
        <CheckCircle2 size={44} className="mx-auto text-emerald-600" />
        <h3 className="mt-4 font-display text-2xl font-bold text-navy">
          Enquiry received, {form.name.split(" ")[0]}.
        </h3>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-600">
          A confirmation is on its way to <strong>{form.email}</strong>. Our
          team will respond by <strong>next working day, first half</strong>.
        </p>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          Urgent? Call {SITE.phoneDisplay} or WhatsApp us anytime.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="enquiry-form" noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Name" required id="f-name">
          <input id="f-name" className={inputCls} data-testid="enquiry-form-name-input" value={form.name} onChange={set("name")} placeholder="Your full name" />
        </Field>
        <Field label="Company" id="f-company">
          <input id="f-company" className={inputCls} data-testid="enquiry-form-company-input" value={form.company} onChange={set("company")} placeholder="Company / firm" />
        </Field>
        <Field label="Designation" id="f-designation">
          <input id="f-designation" className={inputCls} data-testid="enquiry-form-designation-input" value={form.designation} onChange={set("designation")} placeholder="e.g. Purchase Engineer" />
        </Field>
        <Field label="City / State" id="f-city">
          <input id="f-city" className={inputCls} data-testid="enquiry-form-city-input" value={form.city_state} onChange={set("city_state")} placeholder="City, State" />
        </Field>
        <Field label="Email" required id="f-email">
          <input id="f-email" type="email" className={inputCls} data-testid="enquiry-form-email-input" value={form.email} onChange={set("email")} placeholder="name@company.com" />
        </Field>
        <Field label="Phone" required id="f-phone">
          <input id="f-phone" type="tel" className={inputCls} data-testid="enquiry-form-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 …" />
        </Field>
        <Field label="Product of Interest" id="f-product">
          <select id="f-product" className={inputCls} data-testid="enquiry-form-product-select" value={form.product_of_interest} onChange={set("product_of_interest")}>
            {PRODUCT_OPTIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </Field>
        <Field label="Quantity" id="f-quantity">
          <input id="f-quantity" className={inputCls} data-testid="enquiry-form-quantity-input" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 20 kits" />
        </Field>
      </div>
      <Field label="Message" id="f-message">
        <textarea id="f-message" rows={4} className={inputCls} data-testid="enquiry-form-message-input" value={form.message} onChange={set("message")} placeholder="Cable type, voltage class, delivery location, target date…" />
      </Field>

      {/* Honeypot — hidden from humans, irresistible to bots */}
      <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor="f-website">Website</label>
        <input id="f-website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
      </div>

      {error && (
        <p className="border-l-2 border-safety bg-safety-tint px-4 py-3 text-sm text-navy" data-testid="enquiry-form-error" role="alert">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="enquiry-form-submit-button"
        className="group flex w-full items-center justify-center gap-3 bg-safety px-6 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-white transition-all hover:bg-navy disabled:opacity-60"
      >
        {status === "loading" ? (
          <><Loader2 size={16} className="animate-spin" /> Sending…</>
        ) : (
          <><Send size={16} className="transition-transform group-hover:translate-x-1" /> Send Enquiry</>
        )}
      </button>
      <p className="text-center font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
        Spam-protected · We respond by next working day, first half
      </p>
    </form>
  );
};

export default EnquiryForm;
